/**
 * V6.1 — Mon simulateur (Paramètres courtier)
 * Chaque courtier connecté édite UNIQUEMENT ses propres champs simulateur :
 *   slug, calendly_url, display_name, photo_url, phone
 * Backend (V6.0) :
 *   - GET  /api/me/simulator  → charge la config du courtier connecté (session Google)
 *   - PATCH /api/me/simulator → update (check unicité slug côté serveur → 409 si conflit)
 * L'attribution est backend-only : le frontend ne peut jamais forger un user_id.
 */
import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ArrowSquareOut, Broadcast, CheckCircle, WarningCircle, ShareNetwork } from "@phosphor-icons/react";
import { copyToClipboard, canWebShare, nativeShare } from "@/lib/clipboard";
import { ShareSimulatorCard } from "@/components/ShareSimulatorCard";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

// Sanitize slug côté frontend (l'ultime validation reste backend)
function sanitizeSlug(v) {
  return (v || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // retire accents
    .replace(/[^a-z0-9\-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}

export default function SettingsSimulator() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState({
    slug: "", calendly_url: "", display_name: "", photo_url: "", phone: "", public_full_name: "", lead_notification_email: "",
  });
  const [slugError, setSlugError] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/simulator");
      const d = r.data;
      setInitial(d);
      setForm({
        slug: d.slug || "",
        calendly_url: d.calendly_url || "",
        display_name: d.display_name || "",
        photo_url: d.photo_url || "",
        phone: d.phone || "",
        public_full_name: d.public_full_name || "",
        lead_notification_email: d.lead_notification_email || "",
      });
    } catch {
      toast.error("Impossible de charger votre simulateur");
    } finally {
      setLoading(false);
    }
  };

  const dirty = useMemo(() => {
    if (!initial) return false;
    return ["slug", "calendly_url", "display_name", "photo_url", "phone", "public_full_name", "lead_notification_email"]
      .some((k) => (form[k] || "") !== (initial[k] || ""));
  }, [form, initial]);

  const publicUrl = form.slug ? `${PUBLIC_BASE}/s/${form.slug}` : null;

  const handleSlugChange = (v) => {
    const clean = sanitizeSlug(v);
    setForm((f) => ({ ...f, slug: clean }));
    if (!clean) setSlugError("Le slug ne peut pas être vide");
    else setSlugError(null);
  };

  const save = async () => {
    if (saving) return;
    if (!form.slug) { toast.error("Un slug est requis"); return; }
    setSaving(true);
    try {
      const patch = {};
      for (const k of ["slug", "calendly_url", "display_name", "photo_url", "phone", "public_full_name", "lead_notification_email"]) {
        if ((form[k] || "") !== (initial?.[k] || "")) patch[k] = form[k] || "";
      }
      await api.patch("/me/simulator", patch);
      toast.success("✅ Simulateur mis à jour");
      // Recharger pour resync le champ display_name qui peut prendre le fallback name Google
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      if (status === 409) {
        setSlugError("Cette URL est déjà utilisée par un autre courtier.");
        toast.error("Ce slug est déjà pris.");
      } else if (status === 400) {
        setSlugError(detail || "Slug invalide");
        toast.error(detail || "Slug invalide");
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    const ok = await copyToClipboard(publicUrl);
    if (ok) toast.success("✅ Lien copié dans le presse-papier");
    else toast.error("Impossible de copier — sélectionnez et copiez le lien manuellement");
  };

  const shareLink = async () => {
    if (!publicUrl) return;
    const shareData = {
      title: "Simulez votre capacité d'emprunt",
      text: `Découvrez votre capacité d'emprunt en 2 minutes avec ${form.display_name || "moi"} 👇`,
      url: publicUrl,
    };
    try {
      const status = await nativeShare(shareData);
      if (status === "unsupported") {
        // Fallback = copie
        const ok = await copyToClipboard(publicUrl);
        if (ok) toast.success("Lien copié — collez-le où vous voulez");
        else toast.error("Impossible de copier le lien");
      } else if (status === "shared") {
        toast.success("✨ Partagé");
      }
      // cancelled → pas de toast
    } catch (_) {
      toast.error("Le partage a échoué");
    }
  };

  const webShareAvailable = typeof window !== "undefined" && canWebShare({ url: publicUrl || "https://x" });

  if (loading) return <Layout title="Mon simulateur"><div className="text-slate-400 text-sm animate-pulse py-12 text-center">Chargement…</div></Layout>;

  return (
    <Layout title="Mon simulateur" subtitle="Personnalisez votre lien public de simulation" backTo="/">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-simulator">

        {/* Preview lien public */}
        <div className="rounded-2xl p-5 border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white" data-testid="sim-public-preview">
          <div className="flex items-center gap-2 mb-2">
            <Broadcast size={18} weight="fill" className="text-violet-600" />
            <div className="text-[11px] uppercase tracking-widest font-extrabold text-violet-600">Votre lien public</div>
          </div>
          <div className="font-mono text-[13px] sm:text-[15px] font-bold text-slate-900 break-all mb-4" data-testid="sim-public-url">
            {publicUrl || <span className="text-slate-400 font-sans font-normal">Définissez d'abord votre slug…</span>}
          </div>
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
            {webShareAvailable && (
              <button
                type="button" onClick={shareLink} disabled={!publicUrl}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-full text-white font-bold text-[13px] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition shadow-md"
                style={{ background: "linear-gradient(135deg,var(--cf-accent),var(--cf-accent-hover))" }}
                data-testid="sim-share-link-btn"
              >
                <ShareNetwork size={16} weight="bold" /> Partager mon lien
              </button>
            )}
            <button
              type="button" onClick={copyLink} disabled={!publicUrl}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-full bg-slate-900 text-white font-bold text-[13px] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition"
              data-testid="sim-copy-link-btn"
            >
              <Copy size={14} weight="bold" /> Copier le lien
            </button>
            {publicUrl && (
              <a
                href={publicUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-full bg-white text-slate-900 border-2 border-slate-200 font-bold text-[13px] active:scale-95 transition"
                data-testid="sim-open-link-btn"
              >
                <ArrowSquareOut size={14} weight="bold" /> Ouvrir le simulateur
              </a>
            )}
          </div>
        </div>

        <ShareSimulatorCard slug={initial?.slug} />

        {/* Form */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-4">

          <Field
            label="URL publique"
            hint="Uniquement lettres minuscules, chiffres et tirets. Exemple : marie ou marie-dupont"
            error={slugError}
          >
            <div className="flex items-stretch rounded-xl border-2 border-slate-200 focus-within:border-violet-500 overflow-hidden">
              {/* Préfixe : domaine caché sur mobile pour économiser la largeur */}
              <div className="hidden sm:flex items-center px-3 py-3 bg-slate-50 text-slate-500 text-[13px] font-mono border-r border-slate-200 shrink-0">
                {PUBLIC_BASE.replace(/^https?:\/\//, "")}/s/
              </div>
              <div className="flex sm:hidden items-center px-3 py-3 bg-slate-50 text-slate-500 text-[13px] font-mono border-r border-slate-200 shrink-0">
                /s/
              </div>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="marie"
                className="flex-1 min-w-0 px-3 py-3 text-[16px] sm:text-[14px] font-bold text-slate-900 outline-none tabular-nums"
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid="sim-slug-input"
              />
            </div>
          </Field>

          <Field label="Nom affiché" hint="S'affiche en haut du simulateur et dans l'email de RDV">
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              placeholder="Ex : Marie Dupont"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-violet-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              data-testid="sim-display-name-input"
            />
          </Field>

          <Field label="Nom affiché sur le bouton de rappel" hint={`Le prospect verra « Être rappelé par … ». Laissez vide pour utiliser : ${initial?.public_full_name_default || "Prénom NOM"}`}>
            <input
              type="text"
              value={form.public_full_name}
              onChange={(e) => setForm((f) => ({ ...f, public_full_name: e.target.value }))}
              placeholder={initial?.public_full_name_default || "Prénom NOM"}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-violet-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              data-testid="sim-public-full-name-input"
            />
          </Field>

          <Field label="Email de notification des leads" hint={`Par défaut, les notifications sont envoyées à votre adresse de connexion : ${initial?.login_email || "—"}`}>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={form.lead_notification_email}
              onChange={(e) => setForm((f) => ({ ...f, lead_notification_email: e.target.value }))}
              placeholder={initial?.login_email || "leads@cabinet.fr"}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-violet-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              data-testid="sim-lead-notification-email-input"
            />
          </Field>

          <Field label="URL Calendly" hint="Non utilisé dans le parcours public pour le moment — conservé pour un usage ultérieur (ex. lien envoyé après l'appel).">
            <input
              type="url"
              inputMode="url"
              value={form.calendly_url}
              onChange={(e) => setForm((f) => ({ ...f, calendly_url: e.target.value }))}
              placeholder="https://calendly.com/…"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-violet-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-testid="sim-calendly-input"
            />
          </Field>

          <Field label="Téléphone" hint="Optionnel">
            <input
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="06 XX XX XX XX"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-violet-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              data-testid="sim-phone-input"
            />
          </Field>

          <Field label="Photo (URL)" hint="URL d'une image carrée. L'upload direct arrivera bientôt.">
            <div className="flex items-center gap-3">
              {form.photo_url ? (
                <img
                  src={form.photo_url} alt="preview"
                  className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow shrink-0"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-extrabold text-lg shrink-0"
                     style={{ background: "linear-gradient(135deg,var(--cf-primary),var(--cf-accent))" }}>
                  {(form.display_name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                </div>
              )}
              <input
                type="url"
                inputMode="url"
                value={form.photo_url}
                onChange={(e) => setForm((f) => ({ ...f, photo_url: e.target.value }))}
                placeholder="https://…"
                className="flex-1 min-w-0 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-violet-500 outline-none text-[16px] sm:text-[13px] font-bold text-slate-900"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid="sim-photo-url-input"
              />
            </div>
          </Field>

          <div className="pt-3 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
            {dirty ? (
              <span className="text-[11px] text-amber-600 font-bold flex items-center gap-1">
                <WarningCircle size={14} weight="fill" /> Modifications non enregistrées
              </span>
            ) : (
              <span className="text-[11px] text-emerald-600 font-bold flex items-center gap-1">
                <CheckCircle size={14} weight="fill" /> À jour
              </span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving || !!slugError}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full text-white font-extrabold text-[14px] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition shadow-md"
              style={{ background: "linear-gradient(135deg,var(--cf-accent),var(--cf-accent-hover))" }}
              data-testid="sim-save-btn"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>

        <div className="text-[11px] text-slate-400 text-center leading-relaxed">
          Chaque nouveau lead généré depuis votre simulateur atterrit uniquement dans votre CRM.<br />
          Le prospect ne voit aucun montant : il obtient un indice de faisabilité puis demande à être rappelé par vous.
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-extrabold text-slate-500 mb-1">{label}</div>
      {children}
      {hint && !error && <div className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{hint}</div>}
      {error && <div className="text-[11px] text-red-600 font-bold mt-1.5 flex items-center gap-1"><WarningCircle size={12} weight="fill" /> {error}</div>}
    </div>
  );
}
